import { DO_NOT_DISPLAY_THREAD } from 'copilot-toolkit/constants/PromptThread';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { INTERACTIVE_TOOL, INTERACTIVE_TOOL_BUTTON_CLICK } from '../constants/Tools';
import { TOPIC_NAMES } from '../types/Channel';
import { useFunctionQueue, processInstantActions, processButtonClick } from '../utils/interactiveModeUtils';
import { safeParse } from '../utils/jsonSchemaUtils';
import { useCollaborationSession } from './useCollaborationSession';
import { useSendMessageFromAppToCopilot } from './useSendCollaboration';
import { useSendOnCopilotChannel } from '../channel/hooks/useSendOnCopilotChannel';
import { useIsCopilotWidgetActive } from './useIsCopilotWidgetActive';

// Call once per interactive mode session, anywhere in the component tree.
// Owns the session lifecycle and the Copilot channel comms. To contribute
// interactions / starter actions / llm instructions from a nested
// component, use useInteractiveModeActions instead.
// Handles Copilot channel communication, action execution, and state
// publishing. When wrapped by InteractiveModeProvider, merging of
// contributions registered via useInteractiveModeActions
// happens in the provider before values reach this hook.
export function useInteractiveModeSession({
  onCopilotAction,
  onGetCurrentSessionContext = () => {},
  sessionId,
  appName,
  interactiveModeActions = [],
  additionalLLMInstructions,
  starterActions = {
    actions: []
  },
  ignorePathChanges = false,
  defaultToolSet,
  additionalToolSets = [],
  systemConfig,
  aviatorAgentName,
  assetId,
  snapshot,
  trackingProps
}) {
  const sendMessageFromAppToCopilot = useSendMessageFromAppToCopilot();
  const sendAddStarterActions = useSendOnCopilotChannel(TOPIC_NAMES.ADD_STARTER_ACTIONS);
  const isCopilotWidgetActive = useIsCopilotWidgetActive();
  const {
    addToQueue
  } = useFunctionQueue(appName);
  const capturedStatesRef = useRef({});
  const [snapshotValuesByUniqueId, setSnapshotValuesByUniqueId] = useState({});
  const onSnapshotReturned = useCallback((uniqueKey, snapshotValue) => {
    setSnapshotValuesByUniqueId(prev => Object.assign({}, prev, {
      [uniqueKey]: snapshotValue
    }));
  }, []);
  const customFEActions = useMemo(() => {
    return {
      interactions: interactiveModeActions,
      additionalLLMInstructions: additionalLLMInstructions || '',
      pageName: 'Interactive Mode',
      pageDescription: 'Interactive mode actions',
      ignorePathChanges,
      defaultToolSet,
      additionalToolSets,
      systemConfig,
      aviatorAgentName,
      assetId,
      snapshot
    };
  }, [interactiveModeActions, additionalLLMInstructions, ignorePathChanges, defaultToolSet, additionalToolSets, systemConfig, aviatorAgentName, assetId, snapshot]);
  const handleCopilotAction = useCallback(action => {
    var _action$payload;
    if (onCopilotAction) {
      onCopilotAction(action);
    }
    switch (action === null || action === void 0 || (_action$payload = action.payload) === null || _action$payload === void 0 ? void 0 : _action$payload.actionType) {
      case INTERACTIVE_TOOL:
        {
          var _safeParse, _action$payload$messa, _action$payload2, _action$payload3;
          const llmDeterminedActions = (_safeParse = safeParse((_action$payload$messa = action === null || action === void 0 || (_action$payload2 = action.payload) === null || _action$payload2 === void 0 || (_action$payload2 = _action$payload2.message) === null || _action$payload2 === void 0 || (_action$payload2 = _action$payload2.agentPayload) === null || _action$payload2 === void 0 ? void 0 : _action$payload2.jsonContent) !== null && _action$payload$messa !== void 0 ? _action$payload$messa : '')) === null || _safeParse === void 0 ? void 0 : _safeParse.feInteractionsToExecute;

          // Execute actions immediately without showing buttons
          // This will populate capturedStatesRef with original values for undo
          processInstantActions({
            actions: llmDeterminedActions,
            customFEActions,
            capturedStatesRef,
            // Gets populated with original states
            addToQueue,
            sessionId,
            usageTracker: action === null || action === void 0 ? void 0 : action.usageTracker,
            onSnapshotReturned,
            interactionId: action === null || action === void 0 || (_action$payload3 = action.payload) === null || _action$payload3 === void 0 || (_action$payload3 = _action$payload3.message) === null || _action$payload3 === void 0 ? void 0 : _action$payload3.interactionId
          });
        }
        break;
      case INTERACTIVE_TOOL_BUTTON_CLICK:
        // User clicked Apply or Undo button - execute the requested action
        processButtonClick(action, customFEActions, addToQueue, onSnapshotReturned, sessionId);
        break;
      default:
        break;
    }
  }, [addToQueue, customFEActions, onCopilotAction, sessionId, onSnapshotReturned]);

  // Set up communication with Copilot and handle incoming messages
  const {
    sendActionFromAppToCopilot,
    sendCollaborationStateChangeFromAppToCopilot,
    isCopilotReady
  } = useCollaborationSession({
    onCopilotAction: handleCopilotAction,
    onGetCurrentSessionContext,
    sessionId,
    appName,
    plugin: 'InteractiveModePlugin'
  });

  // Send suggested starter prompts to Copilot when widget becomes active
  useEffect(() => {
    if (starterActions && isCopilotWidgetActive) {
      sendAddStarterActions(starterActions);
    }
  }, [starterActions, sendAddStarterActions, isCopilotWidgetActive]);

  // CAPTURED STATES FLOW: Send captured states to plugins so undo buttons work correctly
  useEffect(() => {
    if (customFEActions && isCopilotWidgetActive) {
      // Only include captured states if we have any (for undo functionality)
      const hasCaptures = Object.keys(capturedStatesRef.current).length > 0;
      const hasSnapshots = Object.keys(snapshotValuesByUniqueId).length > 0;
      const stateUpdate = Object.assign({
        customFEActions
      }, hasCaptures && {
        capturedStates: capturedStatesRef.current
      }, hasSnapshots && {
        snapshotValuesByUniqueId
      });
      sendCollaborationStateChangeFromAppToCopilot(stateUpdate);
    }
  }, [customFEActions, isCopilotWidgetActive, sendCollaborationStateChangeFromAppToCopilot, capturedStatesRef, snapshotValuesByUniqueId]);

  // Opens Copilot in Interactive Mode for this page
  const openInteractiveMode = useCallback(() => {
    // TODO: use new hook james made to open.
    sendActionFromAppToCopilot({
      title: 'New thread',
      data: {
        intent: 'text_generate',
        prompt: 'FE_ACTIONS'
      },
      promptData: {
        promptPrefix: DO_NOT_DISPLAY_THREAD,
        commandName: 'customFEAction',
        path: []
      }
    });
  }, [sendActionFromAppToCopilot]);

  // Send a text message to Copilot from the application
  const handleSendMessageFromAppToCopilot = useCallback(({
    messageText
  }) => {
    sendMessageFromAppToCopilot({
      messageText,
      messageMetaInfo: Object.assign({
        source: 'PLUGIN'
      }, (trackingProps === null || trackingProps === void 0 ? void 0 : trackingProps.interactionSource) != null && {
        interactionSource: trackingProps.interactionSource
      })
    });
  }, [sendMessageFromAppToCopilot, trackingProps === null || trackingProps === void 0 ? void 0 : trackingProps.interactionSource]);
  return {
    sendMessageFromAppToCopilot: handleSendMessageFromAppToCopilot,
    isCopilotReady,
    openInteractiveMode
  };
}